import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
	TClientAccess,
	TDirectDebitBanks,
	TDirectDebitCreateResponse,
	TDirectDebitVerifyResult,
	TPaymentMethods,
	TPaymentMethodSetup,
	TPaymentServices,
	TSetupResult,
} from 'services/api/paymentMethods/types';
import {
	TCompleteSetupPayload,
	TDirectDebitCreateRequest,
	TDirectDebitVerifyRequest,
	TMethodIdPayload,
	TPaymentMethodSetupRequest,
	TPaymentMethodsStore,
	TSilentRequest,
	TSummarySuccessPayload,
	TUpdateMethodServicesRequest,
} from './types';

// ==========================================:
export const initialState: TPaymentMethodsStore = {
	list: null,
	listLoading: false,
	summary: null,
	summaryMerchantId: null,
	summaryLoading: false,
	services: null,
	servicesLoading: false,
	methodActionLoading: false,
	updateServicesLoading: false,
	updateServicesError: null,
	setup: null,
	setupLoading: false,
	setupError: null,
	setupCompleting: false,
	setupResult: null,
	banks: null,
	banksLoading: false,
	ddCreate: null,
	ddCreateLoading: false,
	ddVerifyLoading: false,
	ddVerifyResult: null,
	clientAccess: null,
	clientAccessLoading: false,
	clientAccessChecked: false,
};

// ==========================================:
const paymentMethods = createSlice({
	name: '@@paymentMethods',
	initialState,
	reducers: {
		// ---------- list ----------
		getPaymentMethodsRequest: (state) => {
			state.listLoading = true;
		},
		getPaymentMethodsSuccess: (state, action: PayloadAction<TPaymentMethods>) => {
			state.list = action.payload;
			state.listLoading = false;
		},
		getPaymentMethodsFailure: (state) => {
			state.listLoading = false;
		},

		// ---------- summary ----------
		getSummaryRequest: (state, _action: PayloadAction<TSilentRequest>) => {
			state.summaryLoading = true;
		},
		getSummarySuccess: (state, action: PayloadAction<TSummarySuccessPayload>) => {
			state.summary = action.payload.summary;
			state.summaryMerchantId = action.payload.merchantId;
			state.summaryLoading = false;
		},
		getSummaryFailure: (state) => {
			state.summaryLoading = false;
		},

		// ---------- services ----------
		getServicesRequest: (state) => {
			state.servicesLoading = true;
		},
		getServicesSuccess: (state, action: PayloadAction<TPaymentServices>) => {
			state.services = action.payload;
			state.servicesLoading = false;
		},
		getServicesFailure: (state) => {
			state.servicesLoading = false;
		},

		// ---------- list + summary after a change ----------
		reloadPaymentMethodsRequest: () => {},

		// ---------- default / delete ----------
		setDefaultRequest: (state, _action: PayloadAction<TMethodIdPayload>) => {
			state.methodActionLoading = true;
		},
		deleteMethodRequest: (state, _action: PayloadAction<TMethodIdPayload>) => {
			state.methodActionLoading = true;
		},
		methodActionDone: (state) => {
			state.methodActionLoading = false;
		},

		// ---------- services of a method ----------
		updateMethodServicesRequest: (state, _action: PayloadAction<TUpdateMethodServicesRequest>) => {
			state.updateServicesLoading = true;
			state.updateServicesError = null;
		},
		updateMethodServicesSuccess: (state) => {
			state.updateServicesLoading = false;
		},
		updateMethodServicesFailure: (state, action: PayloadAction<string>) => {
			state.updateServicesLoading = false;
			state.updateServicesError = action.payload;
		},

		// ---------- binding (Drop-in) ----------
		startSetupRequest: (state, _action: PayloadAction<TPaymentMethodSetupRequest>) => {
			state.setup = null;
			state.setupResult = null;
			state.setupError = null;
			state.setupLoading = true;
		},
		startSetupSuccess: (state, action: PayloadAction<TPaymentMethodSetup>) => {
			state.setup = action.payload;
			state.setupLoading = false;
		},
		startSetupFailure: (state, action: PayloadAction<string>) => {
			state.setupLoading = false;
			state.setupError = action.payload;
		},
		completeSetupRequest: (state, _action: PayloadAction<TCompleteSetupPayload>) => {
			state.setupCompleting = true;
			state.setupError = null;
		},
		completeSetupSuccess: (state, action: PayloadAction<TSetupResult>) => {
			state.setupResult = action.payload;
			state.setupCompleting = false;
		},
		completeSetupFailure: (state, action: PayloadAction<string>) => {
			state.setupCompleting = false;
			state.setupError = action.payload;
		},
		resetSetup: (state) => {
			state.setup = null;
			state.setupLoading = false;
			state.setupError = null;
			state.setupResult = null;
		},

		// ---------- Direct Debit ----------
		getBanksRequest: (state) => {
			state.banksLoading = true;
		},
		getBanksSuccess: (state, action: PayloadAction<TDirectDebitBanks>) => {
			state.banks = action.payload;
			state.banksLoading = false;
		},
		getBanksFailure: (state) => {
			state.banksLoading = false;
		},
		createDirectDebitRequest: (state, _action: PayloadAction<TDirectDebitCreateRequest>) => {
			state.ddCreate = null;
			state.ddCreateLoading = true;
		},
		createDirectDebitSuccess: (state, action: PayloadAction<TDirectDebitCreateResponse>) => {
			state.ddCreate = action.payload;
			state.ddCreateLoading = false;
		},
		createDirectDebitFailure: (state) => {
			state.ddCreateLoading = false;
		},
		clearDirectDebitCreate: (state) => {
			state.ddCreate = null;
		},
		verifyDirectDebitRequest: (state, _action: PayloadAction<TDirectDebitVerifyRequest>) => {
			state.ddVerifyLoading = true;
			state.ddVerifyResult = null;
		},
		verifyDirectDebitSuccess: (state, action: PayloadAction<TDirectDebitVerifyResult>) => {
			state.ddVerifyLoading = false;
			state.ddVerifyResult = action.payload;
		},
		verifyDirectDebitFailure: (state) => {
			state.ddVerifyLoading = false;
		},
		clearDirectDebitVerifyResult: (state) => {
			state.ddVerifyResult = null;
		},

		// ---------- operator restrictions (client access) ----------
		getClientAccessRequest: (state) => {
			state.clientAccessLoading = true;
		},
		getClientAccessSuccess: (state, action: PayloadAction<TClientAccess>) => {
			state.clientAccess = action.payload;
			state.clientAccessLoading = false;
			state.clientAccessChecked = true;
		},
		// the last known state is kept: the gateway refuses blocked calls with 423 anyway
		getClientAccessFailure: (state) => {
			state.clientAccessLoading = false;
			state.clientAccessChecked = true;
		},

		paymentMethodsInitState: () => initialState,
	},
});

export default paymentMethods.reducer;
export const {
	getPaymentMethodsRequest,
	getPaymentMethodsSuccess,
	getPaymentMethodsFailure,
	getSummaryRequest,
	getSummarySuccess,
	getSummaryFailure,
	getServicesRequest,
	getServicesSuccess,
	getServicesFailure,
	reloadPaymentMethodsRequest,
	setDefaultRequest,
	deleteMethodRequest,
	methodActionDone,
	updateMethodServicesRequest,
	updateMethodServicesSuccess,
	updateMethodServicesFailure,
	startSetupRequest,
	startSetupSuccess,
	startSetupFailure,
	completeSetupRequest,
	completeSetupSuccess,
	completeSetupFailure,
	resetSetup,
	getBanksRequest,
	getBanksSuccess,
	getBanksFailure,
	createDirectDebitRequest,
	createDirectDebitSuccess,
	createDirectDebitFailure,
	clearDirectDebitCreate,
	verifyDirectDebitRequest,
	verifyDirectDebitSuccess,
	verifyDirectDebitFailure,
	clearDirectDebitVerifyResult,
	getClientAccessRequest,
	getClientAccessSuccess,
	getClientAccessFailure,
	paymentMethodsInitState,
} = paymentMethods.actions;
